/**
 * Function Mapping Utility
 * 
 * Provides mapping between Node.js and Python function names
 * and utilities for dynamic backend selection.
 */

// Use Node.js backend (Python backend is now legacy)
const USE_PYTHON_BACKEND = false;

// Function name mapping: Node.js -> Python
const FUNCTION_MAPPING: Record<string, string> = {
  // User and tenant management
  'provisionTenant': 'provision_tenant',
  'calculateUserCredits': 'calculate_user_credits',
  'updateUserRole': 'update_user_role',
  'fixUserDisplayNames': 'fix_user_display_names',
  'debugUser': 'debug_user',

  // Global settings
  'getGlobalSettings': 'get_global_settings',
  'updateGlobalSettings': 'update_global_settings',
  'readGlobalSettings': 'read_global_settings',
  'migrateGlobalSettings': 'migrate_global_settings',
  'fixGlobalAdmin': 'fix_global_admin',

  // Tenant settings
  'initializeTenantSettings': 'initialize_tenant_settings',
  'debugTenantData': 'debug_tenant_data',
  'getTenantInfo': 'get_tenant_info',
  'getTenantSettings': 'get_tenant_settings',
  'updateTenantSettings': 'update_tenant_settings',


  // Presentation management
  'createPresentation': 'create_presentation',
  'updatePresentation': 'update_presentation',
  'initiateChunkedUpload': 'initiate_chunked_upload',
  'getChunkUploadUrl': 'get_chunk_upload_url',
  'confirmChunkUploaded': 'confirm_chunk_uploaded',
  'finalizeChunkedUpload': 'finalize_chunked_upload',
  'getPresentations': 'get_presentations',
  'getPresentation': 'get_presentation',

  // Evaluation Pipeline
  'startEvaluationPipeline': 'start_evaluation_pipeline',
  'getEvaluationProgress': 'get_evaluation_progress',
  'restartFailedEvaluation': 'restart_failed_evaluation',

  // Individual Pipeline Functions
  'processTranscriptionStep': 'process_transcription_step',
  'identifySpeakersStep': 'identify_speakers_step',
  'evaluateStructureStep': 'evaluate_structure_step',
  'evaluateSoftSkillsStep': 'evaluate_soft_skills_step',
  'evaluateKnowledgeStep': 'evaluate_knowledge_step',
  'generateFinalReportStep': 'generate_final_report_step',
  'processTranscriptionStepV2': 'process_transcription_step_v2',
  'executeV2FullPipeline': 'evaluate_all_categories_consolidated',
  'startSequentialEvaluationPipeline': 'start_sequential_evaluation_pipeline',

  // Evaluation Schema Management
  'createEvaluationSchema': 'create_evaluation_schema',
  'updateEvaluationSchema': 'update_evaluation_schema',
  'deleteEvaluationSchema': 'delete_evaluation_schema',
  'getEvaluationSchemas': 'get_evaluation_schemas',
  'cloneGlobalSchema': 'clone_global_schema',
  'assignSchemaToUser': 'assign_schema_to_user',

  // Consultant Management
  'createConsultant': 'create_consultant',
  'updateConsultant': 'update_consultant',
  'deleteConsultant': 'delete_consultant',
  'getConsultants': 'get_consultants',
  'getConsultant': 'get_consultant',

  // Analytics
  'getAnalyticsDashboard': 'get_analytics_dashboard',
  'getConsultantAnalytics': 'get_consultant_analytics',

  // Reporting
  'getPresentationReport': 'get_presentation_report',
  'getRubricDetailView': 'get_rubric_detail_view',
  'getSpeakerAnalysis': 'get_speaker_analysis',
  'exportReportAsPDF': 'export_report_as_pdf',
  'copyReportMarkdown': 'copy_report_markdown',
  'getPresentationReportsList': 'get_presentation_reports_list',
  'regeneratePresentationReport': 'regenerate_presentation_report',
  
  // Zoom Integration
  'getZoomConnectionStatus': 'get_zoom_connection_status',
  'disconnectZoom': 'disconnect_zoom',
  'fetchZoomRecordings': 'fetch_zoom_recordings',
  'importZoomRecording': 'import_zoom_recording',
  'refreshZoomToken': 'refresh_zoom_token',
  
  // Recovery
  'getEvaluationHealth': 'get_evaluation_health',
  'identifyStuckPresentations': 'identify_stuck_presentations',
  'restartEvaluation': 'restart_evaluation',
  'recoverStuckEvaluations': 'recover_stuck_evaluations',
  
  // Evaluation Schema (additional)
  'getEvaluationSchema': 'get_evaluation_schema',
  'setDefaultSchema': 'set_default_schema',
  'getGlobalEvaluationSchemas': 'get_global_evaluation_schemas',
  'validateEvaluationSchema': 'validate_evaluation_schema',
  'getSchemaAnalytics': 'get_schema_analytics',
  
  // Consultant (additional)
  'searchConsultants': 'search_consultants',
  'linkConsultantToUser': 'link_consultant_to_user',
  'getConsultantPresentations': 'get_consultant_presentations',

  // Utility functions
  'healthCheck': 'health_check',
  'setupAdmin': 'setup_admin'
};

// Reverse mapping: Python -> Node.js
const REVERSE_MAPPING: Record<string, string> = Object.fromEntries(
  Object.entries(FUNCTION_MAPPING).map(([nodejs, python]) => [python, nodejs])
);

/**
 * Get the appropriate function name based on backend choice
 * @param {string} nodejsFunctionName - The Node.js function name (or Python name if legacy)
 * @returns {string} The function name to use (Node.js or Python)
 */
export const getFunctionName = (nodejsFunctionName: string): string => {
  if (!USE_PYTHON_BACKEND) {
    // If we are in Node.js mode, check if a Python name was passed in (legacy code)
    // If so, convert it to Node.js name. Otherwise, return the Node.js name as-is.
    const convertedName = REVERSE_MAPPING[nodejsFunctionName] || nodejsFunctionName;
    return convertedName;
  }

  // Python backend mode - convert Node.js name to Python name
  const pythonName = FUNCTION_MAPPING[nodejsFunctionName] || nodejsFunctionName;
  return pythonName;
};

/**
 * Get Node.js function name from Python function name
 * @param {string} pythonFunctionName - The Python function name
 * @returns {string} The equivalent Node.js function name
 */
export const getNodejsFunctionName = (pythonFunctionName: string): string => {
  return REVERSE_MAPPING[pythonFunctionName] || pythonFunctionName;
};

/**
 * Check if currently using Python backend
 * @returns {boolean} True if using Python backend
 */
export const isUsingPythonBackend = (): boolean => {
  return USE_PYTHON_BACKEND;
};

/**
 * Get backend type string
 * @returns {string} 'python' or 'nodejs'
 */
export const getBackendType = (): string => {
  return USE_PYTHON_BACKEND ? 'python' : 'nodejs';
};

/**
 * Get all available function mappings
 * @returns {object} Object with function mappings
 */
export const getAllMappings = (): any => {
  return {
    nodejs_to_python: FUNCTION_MAPPING,
    python_to_nodejs: REVERSE_MAPPING,
    current_backend: getBackendType()
  };
};

/**
 * Migration helper: get both function names
 * @param {string} nodejsFunctionName - The Node.js function name
 * @returns {object} Object with both function names
 */
export const getFunctionNames = (nodejsFunctionName: string): any => {
  return {
    nodejs: nodejsFunctionName,
    python: FUNCTION_MAPPING[nodejsFunctionName] || nodejsFunctionName,
    current: getFunctionName(nodejsFunctionName)
  };
};

// Default export
export default {
  getFunctionName,
  getNodejsFunctionName,
  isUsingPythonBackend,
  getBackendType,
  getAllMappings,
  getFunctionNames,
  FUNCTION_MAPPING,
  REVERSE_MAPPING
};